import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { notificationService } from '../../lib/notificationService'

interface NotificationBadgeProps {
  userId: number
  className?: string
  showCount?: boolean
  onClick?: () => void
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({ 
  userId, 
  className = '', 
  showCount = true,
  onClick 
}) => {
  const { t } = useTranslation('common')
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadUnreadCount()
    
    // Set up polling for real-time updates
    const interval = setInterval(loadUnreadCount, 30000) // Check every 30 seconds
    
    return () => clearInterval(interval)
  }, [userId])

  const loadUnreadCount = async () => {
    try {
      console.log('🔔 Loading notification count for user:', userId);

      // Get unread notification count from the notification service
      const stats = await notificationService.getNotificationStats(userId);
      setUnreadCount(stats.unread || 0);
      console.log('✅ Notification count loaded:', stats.unread);
    } catch (error) {
      console.error('❌ Error loading notification count:', error);
      setUnreadCount(0);
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={`notification-badge ${className}`}>
        <button
          onClick={onClick}
          className="relative p-2 text-gray-400 hover:text-white transition-colors"
        >
          <span className="text-xl">🔔</span>
        </button>
      </div>
    )
  }

  return (
    <div className={`notification-badge ${className}`}>
      <button
        onClick={onClick}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
        title={t('notifications.badge.title', '{{count}} unread notifications', { count: unreadCount })}
      >
        <span className="text-xl">🔔</span>
        
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-sm font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {showCount ? (unreadCount > 99 ? '99+' : unreadCount) : ''}
          </span>
        )}
      </button>
    </div>
  )
}

// Notification dropdown component for quick access
interface NotificationDropdownProps {
  userId: number
  isOpen: boolean
  onClose: () => void
  onViewAll: () => void
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  userId,
  isOpen,
  onClose,
  onViewAll
}) => {
  const { t } = useTranslation('common')
  const [recentNotifications, setRecentNotifications] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isOpen) {
      loadRecentNotifications()
    }
  }, [isOpen, userId])

  const loadRecentNotifications = async () => {
    setLoading(true)
    try {
      const result = await notificationService.getUserNotifications(userId, {
        limit: 5,
        unread_only: false,
        include_archived: false
      })
      setRecentNotifications(result.notifications)
    } catch (error) {
      console.error('Error loading recent notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId, userId)
      setRecentNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      )
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'payment_approved': return '✅'
      case 'payment_rejected': return '❌'
      case 'payment_reversed': return '🔄'
      case 'commission_earned': return '💰'
      case 'referral': return '👥'
      case 'system': return '📢'
      default: return '📬'
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    return `${Math.floor(diffInHours / 24)}d ago`
  }

  if (!isOpen) return null

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-gray-800 rounded-lg border border-gray-700 shadow-xl z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white">{t('notifications.dropdown.heading', 'Recent Notifications')}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto mb-2"></div>
            <p className="text-white text-sm">{t('common.loading', 'Loading...')}</p>
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-4xl mb-2">📬</div>
            <p className="text-white text-sm">{t('notifications.dropdown.empty', 'No recent notifications')}</p>
          </div>
        ) : (
          recentNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 border-b border-gray-700 last:border-b-0 hover:bg-gray-750 transition-colors ${
                !notification.is_read ? 'bg-gray-800/50' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <span className="text-lg flex-shrink-0">
                  {getNotificationIcon(notification.notification_type)}
                </span>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-white text-sm truncate">
                      {notification.title}
                    </h4>
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                    )}
                  </div>
                  
                  <p className="text-gray-300 text-sm line-clamp-2 mb-2">
                    {notification.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white">
                      {formatTimeAgo(notification.created_at)}
                    </span>
                    
                    {!notification.is_read && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {t('notifications.dropdown.markRead', 'Mark read')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-700">
        <button
          onClick={onViewAll}
          className="w-full py-2 px-4 bg-yellow-500 hover:bg-yellow-600 text-black font-medium rounded-lg transition-colors"
        >
          {t('notifications.dropdown.viewAll', 'View All Notifications')}
        </button>
      </div>
    </div>
  )
}
